import React, { useEffect, useState } from 'react';
import { getFormValues } from 'redux-form';
import { Grid, makeStyles, CircularProgress } from '@material-ui/core';
import TypicalAssetAllocation from 'components/RiskAnalysis/TypicalAssetAllocation/TypicalAssetAllocation';
import TypicalAssetAllocationReportChart from 'components/Charts/Report/TypicalAssetAllocation';
import ForecastInvestmentReportChart from 'components/Charts/Report/ForecastGrossInvestmentReturn';
import { BoxWrapper } from 'components/Base';
import StoreProvider from 'store/store';
import {
  MetaType,
  ForecastType,
  RiskCapacityLevelType,
  RiskRequirementLevelType
} from 'store/modules/Meta/types';
import { calculateRiskSummary } from 'utils/riskSummary';
import { defineForecastsType } from 'utils/forecastsTypes';
import RiskSummary from 'components/RiskAnalysis/RiskSummary/RiskSummary';
import ForecastInvestment from '../ForecastInvestment/ForecastInvestment';
import { CustomerType } from 'store/modules/Customers/types';
import api from 'api';
import Router from 'components/Router/Router';
import { errHelper } from 'utils';

const { useParams } = Router;

const useStyles = makeStyles({
  paper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  box: {
    flexDirection: 'column',
    display: 'flex'
  },
  chart: {
    margin: '-8px -8px 8px'
  },
  loaderWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  }
});

const { connect, selectors } = StoreProvider;

interface RiskAnalysisResultProps {
  forecasts: boolean | ForecastType;
  forecastTypes: MetaType;
  riskCapacityLevel: RiskCapacityLevelType;
  formValues: any;
  riskRequirementLevel: RiskRequirementLevelType;
  caseCustomers: Array<CustomerType>;
  hasRiskRequirementCapacityEnabled: boolean;
}

// const dummyData = [
//   { name: 'Risk requirement', value: 2 },
//   { name: 'Risk tolerance', value: 5 },
//   { name: 'Risk capacity', value: 1 },
//   { name: 'Recommended risk profile', value: 7 }
// ];

const RiskAnalysisResults = ({
  forecasts,
  forecastTypes,
  riskCapacityLevel,
  formValues,
  riskRequirementLevel,
  caseCustomers,
  hasRiskRequirementCapacityEnabled
}: RiskAnalysisResultProps) => {
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [allocations, setAllocations] = useState(undefined);
  const classes = useStyles();
  const [forcastsData, setForcasts] = useState(
    defineForecastsType(forecastTypes, forecasts)
  );
  const [riskSummaryValues, setriskSummaryValues] = useState(
    calculateRiskSummary(
      riskRequirementLevel,
      formValues.risk_tolerance,
      riskCapacityLevel,
      formValues.recommended_risk_profile,
      caseCustomers,
      hasRiskRequirementCapacityEnabled,
      formValues.risk_tolerance_customer_2
    )
  );

  const { portfolio } = useParams() as any;

  const { recommended_risk_profile, time_horizon_interval } = formValues;

  useEffect(() => {
    if (recommended_risk_profile && time_horizon_interval) {
      setIsLoadingAssets(true);
      api
        .getTypicalAssetAllocationEvalue({
          portfolio,
          recommendedRiskProfile: formValues.recommended_risk_profile,
          timeHorizon: formValues.time_horizon_interval
        })
        .then(({ data }) => {
          const preparedData = data.map((allocation: any) => ({
            name: allocation.name,
            value: Number(allocation.allocation) * 100
          }));
          setTimeout(() => {
            setAllocations(preparedData);
          });
          setIsLoadingAssets(false);
        })
        .catch((err: any) => {
          errHelper(err);
          setIsLoadingAssets(false);
        });
    }
    // eslint-disable-next-line
  }, [time_horizon_interval, recommended_risk_profile]);

  useEffect(() => {
    setForcasts(defineForecastsType(forecastTypes, forecasts));
  }, [forecastTypes, forecasts]);

  useEffect(() => {
    setriskSummaryValues(
      calculateRiskSummary(
        riskRequirementLevel,
        formValues.risk_tolerance,
        riskCapacityLevel,
        formValues.recommended_risk_profile,
        caseCustomers,
        hasRiskRequirementCapacityEnabled,
        formValues.risk_tolerance_customer_2
      )
    );
  }, [
    riskRequirementLevel,
    formValues.risk_tolerance,
    riskCapacityLevel,
    formValues.recommended_risk_profile,
    caseCustomers,
    hasRiskRequirementCapacityEnabled,
    formValues.risk_tolerance_customer_2
  ]);

  return (
    <Grid item xs={12} sm={12} md={8} lg={8} xl={9} className={classes.box}>
      <Grid container spacing={2} className={classes.chart}>
        <Grid item xs={12} md={12} lg={6}>
          <BoxWrapper title="Risk summary">
            <RiskSummary data={riskSummaryValues} />
          </BoxWrapper>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <BoxWrapper
            title="Forecast gross investment returns p.a."
            helperText={
              <>
                <p>
                  The 'In a very good year' and 'In a very bad year' denote a
                  90% confidence level. The 'In a good year' and 'In a bad year'
                  denote a 50% confidence level.
                </p>
                <p>
                  These are estimates based on historical data and expert
                  opinion from EValue about future long-term possible asset
                  class returns and are not guaranteed - the returns in any year
                  could be outside this range.
                </p>
              </>
            }
          >
            <ForecastInvestment data={forcastsData} />
          </BoxWrapper>
        </Grid>
      </Grid>
      <BoxWrapper
        title="Typical asset allocation"
        helperText="Time-weighted asset allocation based on recommended risk profile"
        headerChildren={
          <React.Fragment>
            {isLoadingAssets && (
              <div className={classes.loaderWrapper}>
                <CircularProgress size="20px" />
              </div>
            )}
          </React.Fragment>
        }
        style={{ padding: '1rem 0' }}
      >
        <TypicalAssetAllocation data={allocations} />
      </BoxWrapper>

      <ForecastInvestmentReportChart data={forcastsData} />
      <TypicalAssetAllocationReportChart data={allocations} />
    </Grid>
  );
};

export default connect((state: any) => ({
  forecasts: selectors.metaSelectors.forecasts(state),
  forecastTypes: selectors.metaSelectors.forecastTypes(state),
  riskCapacityLevel: selectors.metaSelectors.riskCapacityLevel(state),
  formValues: getFormValues('riskProfile')(state),
  riskRequirementLevel: selectors.metaSelectors.riskRequirementLevel(state),
  caseCustomers: selectors.caseSelectors.caseCustomers(state),
  hasRiskRequirementCapacityEnabled:
    selectors.capacityForLossSelectors.hasRiskRequirementCapacityEnabled(state)
}))(RiskAnalysisResults);
